import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { CalendarCheck2 as PresenzeIcon, ClipboardList, TriangleAlert, Users, CalendarDays, Clock3, Shield } from 'lucide-react';
import { presenzeMock } from '../mocks/hr/personaleMockData';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const statoColor = {
  presente: 'success',
  assente: 'error',
  in_ferie: 'default',
  in_malattia: 'error',
  anomalia: 'warning',
  giustificata: 'success',
  aperta: 'warning'
};

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const StatWidgetHr = ({ title, value, icon: Icon, color, onClick, active = false }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      bgcolor: active ? `${color}08` : 'background.paper'
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function HrPresenze() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sourceMode, setSourceMode] = useState('api');
  const [filtroStato, setFiltroStato] = useState('');
  const [cartellinoRows, setCartellinoRows] = useState(presenzeMock.cartellino);
  const [anomaliaSelezionata, setAnomaliaSelezionata] = useState(null);
  const [anomalie, setAnomalie] = useState(presenzeMock.anomalie);
  const [messaggioAzione, setMessaggioAzione] = useState('');
  const [ultimaRigenerazione, setUltimaRigenerazione] = useState('-');
  const [confirmRigeneraOpen, setConfirmRigeneraOpen] = useState(false);
  const [dipendentiOptions, setDipendentiOptions] = useState([]);
  const [selectedDipendenteId, setSelectedDipendenteId] = useState('');
  const isManageUser = useMemo(() => {
    const role = String(user?.role || '').toLowerCase();
    const dep = String(user?.department_name || '').toLowerCase();
    return role === 'admin' || dep.includes('hr') || dep.includes('risorse umane') || dep.includes('personale');
  }, [user?.department_name, user?.role]);
  const isLegalDepartment = useMemo(() => {
    const dep = String(user?.department_name || '').toLowerCase();
    return dep.includes('legale') || dep.includes('legal');
  }, [user?.department_name]);

  const labels = useMemo(() => {
    const base = {
      title: isManageUser ? 'Presenze' : 'Le Tue Presenze',
      subtitle: isManageUser
        ? 'Cartellino, anomalie, rigenerazione e correzioni timbrature.'
        : 'Riepilogo personale cartellino e anomalie.',
      kpiTotal: isManageUser ? 'Giorni in cartellino' : 'Giorni registrati',
      kpiPresent: isManageUser ? 'Presenze piene' : 'Giorni regolari',
      kpiFerie: isManageUser ? 'Giorni in ferie' : 'Ferie nel periodo',
      kpiAnomalie: isManageUser ? 'Anomalie aperte' : 'Anomalie da verificare',
      kpiMalattia: isManageUser ? 'Giorni malattia' : 'Malattia nel periodo',
      legendPresent: 'Presente',
      legendAnomalia: 'Anomalia',
      legendFerie: 'In ferie',
      legendMalattia: 'In malattia',
      tableTitle: isManageUser ? 'Cartellino (preview)' : 'Il tuo cartellino (preview)',
      tableColPrima: 'Prima entrata',
      tableColUltima: 'Ultima uscita',
      tableColLavorate: 'Ore lavorate',
      tableColPreviste: 'Ore previste',
      emptyTable: 'Nessun dato per il filtro selezionato',
      anomaliesTitle: 'Anomalie',
      anomalyDetailTitle: 'Dettaglio anomalia selezionata',
      anomalyFieldDate: 'Data',
      anomalyFieldType: 'Tipo',
      anomalyFieldGravita: 'Gravita',
      anomalyFieldStato: 'Stato',
      anomalyNone: 'Nessuna anomalia rilevata nel periodo.',
      anomalyAction: 'Giustifica anomalia'
    };

    if (!isLegalDepartment) return base;

    return {
      ...base,
      title: isManageUser ? 'Presenze' : 'Le Tue Pratiche',
      subtitle: isManageUser
        ? 'Cartellino, anomalie, rigenerazione e correzioni timbrature.'
        : 'Riepilogo gestione pratiche e anomalie.',
      kpiTotal: isManageUser ? 'Giorni in cartellino' : 'Giorni gestiti',
      kpiPresent: isManageUser ? 'Presenze piene' : 'Giorni regolari',
      kpiFerie: isManageUser ? 'Giorni in ferie' : 'Pausa nel periodo',
      kpiMalattia: isManageUser ? 'Giorni malattia' : 'Assenze nel periodo',
      legendPresent: 'In gestione',
      legendFerie: 'In pausa',
      legendMalattia: 'Assente',
      tableTitle: isManageUser ? 'Cartellino (preview)' : 'Il tuo riepilogo (preview)',
      tableColPrima: 'Inizio gestione',
      tableColUltima: 'Fine gestione',
      tableColLavorate: 'Tempo gestione',
      tableColPreviste: 'Tempo previsto'
    };
  }, [isLegalDepartment, isManageUser]);

  const displayStato = useCallback(
    (value) => {
      if (!isLegalDepartment) return value;
      const v = String(value || '').trim().toLowerCase();
      if (v === 'presente') return 'in_gestione';
      if (v === 'in_ferie') return 'in_pausa';
      if (v === 'in_malattia') return 'assente';
      return value;
    },
    [isLegalDepartment]
  );

  const displayAnomaliaTipo = useCallback(
    (value) => {
      if (!isLegalDepartment) return value;
      const str = String(value || '');
      return str.replace(/^presenza_/, 'gestione_');
    },
    [isLegalDepartment]
  );

  const formatLongItalianDate = (value) => {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const text = new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }).format(d);
    return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '-';
  };

  const formatTime = (value) => {
    if (!value) return '-';
    return String(value).slice(0, 5);
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const raw = String(value);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
    if (/^\d{2}\/\d{2}\/\d{2}$/.test(raw)) return raw;
    const isoPart = raw.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoPart)) {
      const [yyyy, mm, dd] = isoPart.split('-');
      return `${dd}/${mm}/${yyyy}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatHours = (value) => {
    if (value === null || value === undefined || value === '') return '00:00';
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      const total = Math.max(0, Math.round(numeric * 60));
      const hh = String(Math.floor(total / 60)).padStart(2, '0');
      const mm = String(total % 60).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    return String(value);
  };

  const enumerateIsoDays = (startIso, endIso) => {
    const out = [];
    const s = new Date(`${startIso}T00:00:00`);
    const e = new Date(`${endIso}T00:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || s > e) return out;
    const cur = new Date(s);
    while (cur <= e) {
      out.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setMessaggioAzione('');
    const today = new Date();
    const todayIso = today.toISOString().slice(0, 10);
    let dataDa = todayIso;
    let dataA = todayIso;
    if (isManageUser) {
      const from = new Date();
      from.setDate(today.getDate() - 30);
      dataDa = from.toISOString().slice(0, 10);
      dataA = todayIso;
    }
    if (isManageUser && !selectedDipendenteId) {
      setCartellinoRows([]);
      setAnomalie([]);
      setAnomaliaSelezionata(null);
      setLoading(false);
      return;
    }
    try {
      const params = { data_da: dataDa, data_a: dataA };
      if (isManageUser && selectedDipendenteId) params.dipendente_id = selectedDipendenteId;
      const [presRes, anomRes, richRes] = await Promise.all([
        api.get('/presenze-giornaliere', { params }),
        api.get('/anomalie', { params }),
        api.get('/richieste-assenza', { params: { ...params, stato: 'approvata' } })
      ]);

      const malattiaDays = new Set();
      (richRes.data || []).forEach((r) => {
        const code = String(r.tipo_assenza_codice || '').toLowerCase();
        const name = String(r.tipo_assenza_nome || '').toLowerCase();
        const isMalattia = code.includes('malatt') || name.includes('malatt');
        if (!isMalattia) return;
        const start = String(r.data_inizio || '').slice(0, 10);
        const end = String(r.data_fine || r.data_inizio || '').slice(0, 10);
        if (!start) return;
        const overlapStart = start > dataDa ? start : dataDa;
        const overlapEnd = end < dataA ? end : dataA;
        enumerateIsoDays(overlapStart, overlapEnd).forEach((d) => malattiaDays.add(d));
      });

      const presRows = (presRes.data || []).map((row) => {
        const dataIso = String(row.data_lavoro || '').slice(0, 10);
        return {
          id: row.id,
          data: formatDate(dataIso),
          data_iso: dataIso,
          prima_entrata: formatTime(row.prima_entrata),
          ultima_uscita: formatTime(row.ultima_uscita),
          ore_lavorate: formatHours(row.ore_lavorate),
          ore_previste: formatHours(row.ore_previste),
          stato: malattiaDays.has(dataIso) ? 'in_malattia' : (row.stato_presenza || 'assente')
        };
      });

      const existingIso = new Set(presRows.map((r) => r.data_iso));
      malattiaDays.forEach((iso) => {
        if (existingIso.has(iso)) return;
        presRows.push({
          id: `mal-${iso}`,
          data: formatDate(iso),
          data_iso: iso,
          prima_entrata: '-',
          ultima_uscita: '-',
          ore_lavorate: '00:00',
          ore_previste: '00:00',
          stato: 'in_malattia'
        });
      });
      presRows.sort((a, b) => String(b.data_iso || '').localeCompare(String(a.data_iso || '')));

      const anomRows = (anomRes.data || []).map((row) => ({
        id: row.id,
        data: formatDate(row.data_lavoro),
        tipo: row.tipo_anomalia,
        gravita: row.gravita,
        stato: row.stato,
        note_giustificazione: row.note_giustificazione || ''
      }));

      setCartellinoRows(presRows);
      setAnomalie(anomRows);
      setAnomaliaSelezionata((prev) => {
        if (!anomRows.length) return null;
        if (!prev) return anomRows[0];
        return anomRows.find((a) => a.id === prev.id) || anomRows[0];
      });
      setSourceMode('api');
    } catch (error) {
      console.error('Errore caricamento presenze/anomalie:', error);
      setCartellinoRows(presenzeMock.cartellino);
      setAnomalie(presenzeMock.anomalie);
      setAnomaliaSelezionata(presenzeMock.anomalie[0] || null);
      setSourceMode('mock');
    } finally {
      setLoading(false);
    }
  }, [isManageUser, selectedDipendenteId]);

  const loadDipendenti = useCallback(async () => {
    if (!isManageUser) return;
    try {
      const res = await api.get('/dipendenti', { params: { limit: 300, offset: 0, sortBy: 'cognome', sortDir: 'ASC' } });
      const opts = (res.data?.items || []).map((d) => ({
        id: String(d.id),
        label: `${d.cognome || ''} ${d.nome || ''}`.trim()
      }));
      setDipendentiOptions(opts);
      if (!selectedDipendenteId && opts.length) setSelectedDipendenteId(opts[0].id);
    } catch (error) {
      console.error('Errore caricamento dipendenti:', error);
      setDipendentiOptions([]);
    }
  }, [isManageUser, selectedDipendenteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadDipendenti();
  }, [loadDipendenti]);

  const cartellinoFiltrato = useMemo(
    () => cartellinoRows.filter((row) => (filtroStato ? row.stato === filtroStato : true)),
    [cartellinoRows, filtroStato]
  );

  const kpi = useMemo(() => {
    const totale = cartellinoRows.length;
    const presenti = cartellinoRows.filter((row) => row.stato === 'presente').length;
    const ferie = cartellinoRows.filter((row) => row.stato === 'in_ferie').length;
    const malattia = cartellinoRows.filter((row) => row.stato === 'in_malattia').length;
    const anomalieAperte = anomalie.filter((a) => a.stato === 'aperta').length;
    return { totale, presenti, ferie, malattia, anomalieAperte };
  }, [anomalie, cartellinoRows]);
  const selectedDipendente = dipendentiOptions.find((d) => String(d.id) === String(selectedDipendenteId)) || null;

  const handleRigenera = async () => {
    if (!isManageUser) {
      toast.error('Operazione consentita solo a HR/Admin');
      return;
    }
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    setUltimaRigenerazione(`${hh}:${mm}`);

    if (sourceMode !== 'api') {
      setMessaggioAzione('Rigenerazione demo completata');
      return;
    }

    try {
      setSaving(true);
      const dataLavoro = new Date().toISOString().slice(0, 10);
      await api.post('/presenze-giornaliere/rigenera', { data_lavoro: dataLavoro });
      setMessaggioAzione('Rigenerazione presenze completata');
      toast.success('Rigenerazione completata');
      await loadData();
    } catch (error) {
      console.error('Errore rigenerazione presenze:', error);
      toast.error(error.response?.data?.message || 'Errore durante rigenerazione presenze');
    } finally {
      setSaving(false);
    }
  };

  const handleGiustificaAnomalia = async () => {
    if (!isManageUser) {
      toast.error('Operazione consentita solo a HR/Admin');
      return;
    }
    if (!anomaliaSelezionata?.id) return;
    if (sourceMode !== 'api') {
      setAnomalie((prev) => prev.map((a) => (a.id === anomaliaSelezionata.id ? { ...a, stato: 'giustificata' } : a)));
      setAnomaliaSelezionata((prev) => (prev ? { ...prev, stato: 'giustificata' } : prev));
      setMessaggioAzione('Anomalia giustificata (demo)');
      return;
    }

    try {
      setSaving(true);
      await api.post(`/anomalie/${anomaliaSelezionata.id}/giustifica`, {});
      setMessaggioAzione('Anomalia giustificata');
      toast.success('Anomalia giustificata');
      await loadData();
    } catch (error) {
      console.error('Errore giustificazione anomalia:', error);
      toast.error(error.response?.data?.message || 'Errore durante giustificazione anomalia');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <PresenzeIcon size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {labels.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {labels.subtitle}
          </Typography>
        </Box>
        {isManageUser && (
          <Chip
            icon={<Shield size={14} />}
            label="Vista: Admin/HR"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ ml: 'auto', fontWeight: 600 }}
          />
        )}
      </Box>

      <Grid container spacing={2} columns={{ xs: 12, md: 20 }} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatWidgetHr
            title={labels.kpiTotal}
            value={kpi.totale}
            icon={CalendarDays}
            color="#2563eb"
            onClick={() => setFiltroStato('')}
            active={filtroStato === ''}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatWidgetHr
            title={labels.kpiPresent}
            value={kpi.presenti}
            icon={Users}
            color="#22c55e"
            onClick={() => setFiltroStato('presente')}
            active={filtroStato === 'presente'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatWidgetHr
            title={labels.kpiFerie}
            value={kpi.ferie}
            icon={Clock3}
            color="#6366f1"
            onClick={() => setFiltroStato('in_ferie')}
            active={filtroStato === 'in_ferie'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatWidgetHr
            title={labels.kpiAnomalie}
            value={kpi.anomalieAperte}
            icon={TriangleAlert}
            color="#ef4444"
            onClick={() => setFiltroStato('anomalia')}
            active={filtroStato === 'anomalia'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatWidgetHr
            title={labels.kpiMalattia}
            value={kpi.malattia}
            icon={PresenzeIcon}
            color="#ef4444"
            onClick={() => setFiltroStato('in_malattia')}
            active={filtroStato === 'in_malattia'}
          />
        </Grid>
      </Grid>
      {isManageUser && (
        <StyledCard sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={dipendentiOptions}
                value={selectedDipendente}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                onChange={(_, val) => setSelectedDipendenteId(val ? String(val.id) : '')}
                renderInput={(params) => <TextField {...params} label="Dipendente mostrato" />}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Periodo mostrato: ultimi 30 giorni ({formatDate(new Date(Date.now() - (30 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10))} - {formatDate(new Date().toISOString().slice(0, 10))})
              </Typography>
            </Grid>
          </Grid>
        </StyledCard>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip size="small" label={labels.legendPresent} color="success" variant="outlined" />
        <Chip size="small" label={labels.legendAnomalia} color="warning" variant="outlined" />
        <Chip size="small" label={labels.legendFerie} variant="outlined" />
        <Chip size="small" label={labels.legendMalattia} color="error" variant="outlined" />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <StyledCard>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(145, 158, 171, 0.24)', display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '50%',
                    bgcolor: 'rgba(99,102,241,0.10)',
                    color: '#6366f1',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ClipboardList size={16} />
                </Box>
                <Typography variant="h6">{labels.tableTitle}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!isManageUser && (
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Oggi {formatLongItalianDate(new Date())}
                  </Typography>
                )}
                {isManageUser && (
                  <Button
                    size="small"
                    color="warning"
                    variant="contained"
                    onClick={() => setConfirmRigeneraOpen(true)}
                    disabled={saving}
                  >
                    {saving ? 'Rigenerazione...' : 'Rigenera presenze'}
                  </Button>
                )}
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'rgba(145, 158, 171, 0.08)' }}>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>{labels.tableColPrima}</TableCell>
                    <TableCell>{labels.tableColUltima}</TableCell>
                    <TableCell>{labels.tableColLavorate}</TableCell>
                    <TableCell>{labels.tableColPreviste}</TableCell>
                    <TableCell>Stato</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 2.5 }}>
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && cartellinoFiltrato.map((row) => (
                    <TableRow key={row.data}>
                      <TableCell>{row.data}</TableCell>
                      <TableCell>{row.prima_entrata}</TableCell>
                      <TableCell>{row.ultima_uscita}</TableCell>
                      <TableCell>{row.ore_lavorate}</TableCell>
                      <TableCell>{row.ore_previste}</TableCell>
                      <TableCell>
                        <Chip size="small" label={displayStato(row.stato)} color={statoColor[row.stato] || 'default'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && cartellinoFiltrato.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {labels.emptyTable}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <StyledCard>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(145, 158, 171, 0.24)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: '50%',
                  bgcolor: 'rgba(239,68,68,0.10)',
                  color: '#ef4444',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <TriangleAlert size={16} />
              </Box>
              <Typography variant="h6">{labels.anomaliesTitle}</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Ultima rigenerazione: {ultimaRigenerazione}
              </Typography>
              {loading && (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!loading && anomalie.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {labels.anomalyNone}
                </Typography>
              )}
              {anomalie.map((item) => (
                <Box
                  key={item.id || `${item.data}-${item.tipo}`}
                  sx={{
                    mb: 1.5,
                    p: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid rgba(145, 158, 171, 0.24)',
                    bgcolor:
                      anomaliaSelezionata?.data === item.data && anomaliaSelezionata?.tipo === item.tipo
                        ? 'rgba(79, 70, 229, 0.08)'
                        : 'transparent'
                  }}
                  onClick={() => setAnomaliaSelezionata(item)}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayAnomaliaTipo(item.tipo)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(item.data)} - gravita {item.gravita} - stato {item.stato}
                  </Typography>
                </Box>
              ))}
              {anomaliaSelezionata && (
                <Card variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, mt: 2 }}>
                  <Typography variant="subtitle2">{labels.anomalyDetailTitle}</Typography>
                  <Typography variant="body2">{labels.anomalyFieldDate}: {formatDate(anomaliaSelezionata.data)}</Typography>
                  <Typography variant="body2">{labels.anomalyFieldType}: {displayAnomaliaTipo(anomaliaSelezionata.tipo)}</Typography>
                  <Typography variant="body2">{labels.anomalyFieldGravita}: {anomaliaSelezionata.gravita}</Typography>
                  <Typography variant="body2">
                    {labels.anomalyFieldStato}:{' '}
                    <Chip
                      size="small"
                      label={anomaliaSelezionata.stato}
                      color={statoColor[anomaliaSelezionata.stato] || 'default'}
                      variant="outlined"
                    />
                  </Typography>
                  <Box sx={{ mt: 1.25 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleGiustificaAnomalia}
                      disabled={saving || anomaliaSelezionata.stato === 'giustificata' || !isManageUser}
                    >
                      {labels.anomalyAction}
                    </Button>
                  </Box>
                </Card>
              )}
              {messaggioAzione && (
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  {messaggioAzione}
                </Typography>
              )}
            </Box>
          </StyledCard>
        </Grid>
      </Grid>

      <Dialog open={confirmRigeneraOpen} onClose={() => setConfirmRigeneraOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma Rigenerazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Confermi la rigenerazione presenze della giornata odierna?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRigeneraOpen(false)} disabled={saving}>Annulla</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              await handleRigenera();
              setConfirmRigeneraOpen(false);
            }}
            disabled={saving}
          >
            {saving ? 'Rigenerazione...' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
